import { CommunicationJSON } from './communication-json.model';
import { Utilities } from 'src/app/utilities/utilities';
import {
  STATUS_QUEUED,
  STATUS_REJECTED,
  STATUS_SENT,
} from './communication-status.constants';

export class Communication {
  public readonly id: number;

  public readonly datetime: Date;

  public readonly datetimeUtc: string | null;

  public readonly clientID: number;

  public readonly title: string;

  public readonly message: string;

  public readonly status: number;

  public readonly clientEmail: string;

  public readonly clientPhone: string;

  public readonly sender: string;

  public readonly communicationType: string;

  public readonly emailCommunicationType: number;

  public readonly deliveryStatus: number | null;

  public readonly externalMessageID: string | null;

  public readonly opened: number;

  public readonly clicked: number;

  public readonly deliveredAt: string | null;

  // Campaign-level aggregates — only bulk communication history rows carry
  // them. `tracked` is null (not false) on a row that carries no campaign log
  // at all, so a caller can tell "sent before tracking existed" apart from
  // "this isn't a campaign row".
  public readonly recipientCount: number | null;

  public readonly tracked: boolean | null;

  public readonly sentCount: number | null;

  public readonly delivered: number | null;

  public readonly uniqueOpened: number | null;

  public readonly uniqueClicked: number | null;

  public readonly bounced: number | null;

  public readonly failed: number | null;

  public readonly trackedLink: boolean;

  public readonly inProgress: boolean;

  public readonly costMinor: number | null;

  public readonly sentMessage: string | null;

  public readonly chargePending: boolean;

  constructor(options?: CommunicationJSON) {
    options = options || {};
    this.id = options.id;
    this.datetime = options.datetime;
    // Absent stays absent: an API build that never sent an instant must not be
    // papered over with the display string (see getDateTimeUtc).
    this.datetimeUtc =
      options.datetimeUtc === undefined ? null : options.datetimeUtc;
    this.clientID = options.clientID;
    this.title = options.title;
    this.message = options.message;
    this.status = options.status;
    this.clientEmail = options.clientEmail;
    this.clientPhone = options.clientPhone;
    this.sender = options.sender;
    this.communicationType = options.communicationType;
    this.emailCommunicationType = options.emailCommunicationType;
    this.deliveryStatus =
      options.deliveryStatus === undefined ? null : options.deliveryStatus;
    this.externalMessageID =
      options.externalMessageID === undefined ? null : options.externalMessageID;
    this.opened = options.opened || 0;
    this.clicked = options.clicked || 0;
    this.deliveredAt =
      options.deliveredAt === undefined ? null : options.deliveredAt;
    this.recipientCount = Communication.nullable(options.recipientCount);
    this.tracked =
      options.tracked === undefined || options.tracked === null
        ? null
        : !!options.tracked;
    this.sentCount = Communication.nullable(options.sentCount);
    this.delivered = Communication.nullable(options.delivered);
    this.uniqueOpened = Communication.nullable(options.uniqueOpened);
    this.uniqueClicked = Communication.nullable(options.uniqueClicked);
    this.bounced = Communication.nullable(options.bounced);
    this.failed = Communication.nullable(options.failed);
    this.trackedLink = !!options.hasTrackedLink;
    this.inProgress = !!options.inProgress;
    this.costMinor = Communication.nullable(options.costMinor);
    this.sentMessage = options.sentMessage || null;
    this.chargePending = !!options.chargePending;
  }

  /** Absent stays absent: a missing count is null, never a fabricated 0. */
  private static nullable(value: number | null | undefined): number | null {
    return value === undefined || value === null ? null : value;
  }

  public static parse(params: Object): Communication {
    return new Communication({
      id: params['id'],
      datetime: params['datetime'],
      datetimeUtc: params['datetimeUtc'],
      clientID: params['clientID'],
      title: params['title'],
      message: params['message'],
      status: params['status'],
      clientEmail: params['clientEmail'],
      clientPhone: params['clientPhone'],
      sender: params['sender'],
      communicationType: params['communicationType'],
      emailCommunicationType: params['emailCommunicationType'],
      deliveryStatus: params['deliveryStatus'],
      externalMessageID: params['externalMessageID'],
      opened: params['opened'],
      clicked: params['clicked'],
      deliveredAt: params['deliveredAt'],
      recipientCount: params['recipientCount'],
      tracked: params['tracked'],
      sentCount: params['sentCount'],
      delivered: params['delivered'],
      uniqueOpened: params['uniqueOpened'],
      uniqueClicked: params['uniqueClicked'],
      bounced: params['bounced'],
      failed: params['failed'],
      hasTrackedLink: params['hasTrackedLink'],
      inProgress: params['inProgress'],
      costMinor: params['costMinor'],
      sentMessage: params['sentMessage'],
      chargePending: params['chargePending']
    });
  }

  public getId(): number {
    return this.id;
  }

  /**
   * The send as the salon's clock read it — DISPLAY ONLY. The API formats it
   * salon-local with no timezone designator, so parsing it reads it as the
   * *device's* local time, wrong by the salon's UTC offset (KTD-11). Render it
   * with the `salonMonsterDate` pipe; never measure it against `now`.
   */
  public getDateTime(): Date {
    return this.datetime;
  }

  /**
   * The same send as an INSTANT: ISO-8601 with a `Z`, straight from the API's
   * `datetimeUtc`. This — not `getDateTime()` — is what elapsed-time
   * arithmetic takes, above all smr-delivery-status's pending -> "Sent"
   * fall-through (R3), whose SMS window is fifteen minutes and so cannot
   * absorb a salon's UTC offset.
   *
   * Null when the API build predates the field. Deliberately not backfilled
   * from `datetime`: that would dress a wall-clock reading up as an instant,
   * which is exactly the wrong answer this pair exists to prevent. Callers
   * that need to degrade gracefully hand the display string to the status
   * component instead, which refuses it and holds its pending label.
   */
  public getDateTimeUtc(): string | null {
    return this.datetimeUtc;
  }
  /**
   * Converts a UTC datetime into the salon's timezone. Reserved for UTC
   * values: `datetime` from the history list is ALREADY salon-local with no
   * offset suffix, so passing it here parses it as device-local and then
   * shifts it again — the double conversion only cancels when the device sits
   * in the salon's timezone (KTD-11). Render list datetimes with
   * `getDateTime()` and the `salonMonsterDate` pipe instead.
   */
  public getDateISO(timeZone: string): string {
    const datetime = new Date(this.datetime);
    return datetime.toLocaleString('en-US', { timeZone });
  }

  public getClientId(): number {
    return this.clientID;
  }

  public getTitle(): string {
    return this.title;
  }

  public getMessage(): string {
    return this.message;
  }

  /**
   * What recipients actually received. A bulk text is normalized before it
   * goes out -- filtered keywords removed, salon prefix and opt-out line added --
   * so the raw `message` can differ from what arrived. Falls back to
   * `message` when the API predates the field.
   */
  public getSentMessage(): string {
    return this.sentMessage || this.message;
  }

  /** True while the send's charge is in flight or waiting on the card. */
  public isChargePending(): boolean {
    return this.chargePending;
  }

  public getStatus(): number {
    return this.status;
  }

  public getClientEmail(): string {
    return this.clientEmail;
  }

  public getClientPhone(): string {
    return this.clientPhone;
  }

  public getSender(): string {
    return this.sender;
  }

  public getCommunicationType(): string {
    return this.communicationType;
  }

  public getEmailCommunicationType(): number {
    return this.emailCommunicationType;
  }

  /**
   * Whether this row has real per-recipient delivery tracking. Rows the
   * provider rejected outright (deliveryStatus 8) have no externalMessageID
   * but ARE tracked (KTD-4) — they must never read as untracked. Legacy
   * `status` is NEVER consulted: its vocabulary conflicts with the tracked
   * one (0 = failed legacy vs 0 = queued tracked).
   */
  public isTracked(): boolean {
    return (
      this.externalMessageID != null ||
      (this.deliveryStatus != null &&
        this.deliveryStatus >= STATUS_SENT &&
        this.deliveryStatus <= STATUS_REJECTED)
    );
  }

  /**
   * Tracked delivery status with NULL-as-QUEUED normalization: a tracked row
   * with a NULL deliveryStatus is queued (0). Untracked rows return null.
   */
  public getDeliveryStatus(): number | null {
    if (!this.isTracked()) {
      return this.deliveryStatus;
    }
    return this.deliveryStatus == null ? STATUS_QUEUED : this.deliveryStatus;
  }

  public getExternalMessageID(): string | null {
    return this.externalMessageID;
  }

  public getOpened(): number {
    return this.opened;
  }

  public getClicked(): number {
    return this.clicked;
  }

  public getDeliveredAt(): string | null {
    return this.deliveredAt;
  }

  /**
   * Campaign-level tracking flag: true when the send has a delivery log, false
   * when it predates tracking (R27), null when the row carries no campaign log
   * at all (a per-message row, or a legacy list payload). Distinct from
   * `isTracked()`, which answers the same question for one recipient.
   */
  public isCampaignTracked(): boolean | null {
    return this.tracked;
  }

  public getRecipientCount(): number | null {
    return this.recipientCount;
  }

  public getSentCount(): number | null {
    return this.sentCount;
  }

  public getDelivered(): number | null {
    return this.delivered;
  }

  public getUniqueOpened(): number | null {
    return this.uniqueOpened;
  }

  public getUniqueClicked(): number | null {
    return this.uniqueClicked;
  }

  public getBounced(): number | null {
    return this.bounced;
  }

  public getFailed(): number | null {
    return this.failed;
  }

  /**
   * Whether the campaign contained a link that could register a click. A
   * campaign without one must never be shown a 0% click rate (AE9).
   */
  public hasTrackedLink(): boolean {
    return this.trackedLink;
  }

  public isInProgress(): boolean {
    return this.inProgress;
  }

  /**
   * What a bulk text send cost the salon, in minor units, net of refunds.
   * Null when no charge was taken — never 0, which would read as a free send.
   */
  public getCostMinor(): number | null {
    return this.costMinor;
  }

  public getDeliveredRate(): number | null {
    return Communication.rate(this.delivered, this.sentCount);
  }

  public getOpenedRate(): number | null {
    return Communication.rate(this.uniqueOpened, this.delivered);
  }

  public getClickedRate(): number | null {
    return Communication.rate(this.uniqueClicked, this.delivered);
  }

  /** Whole-number percentage, or null when there is no real denominator. */
  private static rate(
    numerator: number | null,
    denominator: number | null
  ): number | null {
    if (!denominator || numerator == null) {
      return null;
    }
    return Math.round((numerator / denominator) * 100);
  }

  public getChannel(): 'sms' | 'email' {
    return this.communicationType === 'sms' ||
      this.communicationType === 'bulk_text'
      ? 'sms'
      : 'email';
  }

  public getTimeElapsedInMinutes(): number {
    return Utilities.timeDiffInMinutes(this.getDateTime(), new Date());
  }

  public getFormattedTimeElapsed(): string {
    // let elapsed: number = this.getTimeElapsedInMinutes();
    const years: number = Utilities.timeDiffInYears(
      this.getDateTime(),
      new Date()
    );
    const months: number = Utilities.timeDiffInMonths(
      this.getDateTime(),
      new Date()
    );
    const days: number = Utilities.daysDiff(this.getDateTime(), new Date());
    const hours: number = Utilities.timeDiffInHours(
      this.getDateTime(),
      new Date()
    );
    const minutes: number = Utilities.timeDiffInMinutes(
      this.getDateTime(),
      new Date()
    );

    if (years > 1) {
      return `${Math.floor(years)} ${years > 1 ? 'years' : 'year'} ago`;
    } else if (months > 1) {
      return `${Math.floor(months)} ${months > 1 ? 'months' : 'month'} ago`;
    } else if (days > 1) {
      return `${Math.floor(days)} ${days > 1 ? 'days' : 'day'} ago`;
    } else if (hours > 1) {
      return `${Math.floor(hours)} ${hours > 1 ? 'hours' : 'hour'} ago`;
    } else if (minutes > 1) {
      return `${Math.floor(minutes)} ${minutes > 1 ? 'minutes' : 'minute'} ago`;
    } else {
      return '';
    }

    // if (elapsed < 60) {
    //   return elapsed + ' minutes ago';
    // } else {
    //   const days: number = elapsed / (24*60);
    //   const hours: number = Math.floor(days % 60);
    //   const minutes: number = days % 60;

    // return `${days} ${days > 0 ? 'days' : 'day'} ${hours} ${hours > 1 ? 'hours' : 'hour'} and ${minutes} ${minutes > 0 ? 'minutes' : 'minute'} ago`;
    // }
  }
}
